import type {
  PublicCombineMeasurementResponseRecord,
  PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData,
} from '@on3/api';
import { Check } from '@on3/ui-lib/components/Svg';
import { useAuth } from '@on3/ui-lib/src/contexts/AuthProvider';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import useAthlete from 'contexts/AthleteProvider';

import { CombineDataModule } from './CombineDataModule';
import { PlayerVitalAverageItem } from './PlayerVitalAverageItem';
import styles from './PlayerVitalAverages.module.scss';
import { PremiumBlock } from './PremiumBlock/PremiumBlock';

interface IPlayerVitalAverages {
  vitalAverages: PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData;
  className?: string;
  showViewAll?: boolean;
  combineData?: PublicCombineMeasurementResponseRecord[];
}
export const PlayerVitalAverages = ({
  vitalAverages,
  className,
  showViewAll,
  combineData,
}: IPlayerVitalAverages) => {
  const { user } = useAuth();
  const { relatedModel, list } = vitalAverages;
  const { athlete } = useAthlete();
  const orgLevel =
    athlete?.organizationLevel === 'HighSchool'
      ? 'High School'
      : athlete?.organizationLevel;
  const showVitals = user?.has;

  const source = slugify(athlete?.name || 'athlete');

  return (
    <section className={className}>
      <div className={styles.vitalAveragesLegend}>
        <div className={styles.legendContainerLeft}>
          <div className={styles.imageNameContainer}>
            <On3Avatar
              altText={`${athlete?.name ?? ''} Avatar`}
              className={styles.avatar}
              domain={athlete?.defaultAsset?.domain}
              height={40}
              source={athlete?.defaultAsset?.source}
              width={40}
            />
            <Text className={styles.nameLabelContainer} component="div">
              <span className={styles.name}>{athlete?.name}</span>
              <span className={styles.label}>{orgLevel}</span>
            </Text>
          </div>
          <Text
            className={clsx(styles.nameLabelContainer, styles.top300)}
            component="div"
          >
            <span className={styles.name}>
              Top 300 {relatedModel?.position?.abbreviation} Avg
            </span>
            <span className={styles.label}>
              Avg. Last {relatedModel?.numberOfYears} Years
            </span>
          </Text>
          <div className={clsx(styles.nameLabelContainer, styles.drafted)}>
            <span className={styles.name}>
              {relatedModel?.position?.sportKey === 2 ? 'NBA' : 'NFL'} Combine
              Drafted
            </span>
            <span className={styles.label}>
              Avg. Last {relatedModel?.numberOfYears} Years
            </span>
          </div>
        </div>

        <div className={styles.verifiedContainer}>
          <span className={styles.checkmarkContainer}>
            <Check className={styles.checkmark} />
          </span>
          <Text
            className={styles.verifiedText}
            component="span"
            variant="subtitle1"
          >
            on3 verified
          </Text>
        </div>
      </div>

      {showVitals && (
        <>
          <ul className={styles.vitalAveragesList}>
            {list?.map((vital, index) => {
              if (vital?.currentPersonMeasurement) {
                return (
                  <li key={`${vital.measurementKey}-${index}`}>
                    <PlayerVitalAverageItem {...vital} />
                  </li>
                );
              }

              return null;
            })}
          </ul>
          {!!combineData?.length && (
            <CombineDataModule combineData={combineData} />
          )}
          {showViewAll && (
            <footer className={styles.footer}>
              <TextLink
                className={styles.viewAllLink}
                href={`/db/${athlete?.slug}/vitals/`}
                variant="subtitle1"
              >
                Vitals Index
              </TextLink>
            </footer>
          )}
        </>
      )}

      {!showVitals && (
        <PremiumBlock
          title="To unlock player vitals,"
          utmCampaign="player_vitals"
          utmContent="subscribe_link"
          utmMedium="web"
          utmSource={source}
        />
      )}
    </section>
  );
};
